import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/** How long the sheet takes to slide away once it has nothing left to show; matches the CSS transition. */
export const EXIT_MS = 320;

/**
 * Keeps the dock mounted for one exit transition after it has nothing to
 * show, so it sinks away instead of vanishing.
 */
export const useDeparture = (present: boolean): { mounted: boolean; leaving: boolean } => {
  const [mounted, setMounted] = useState(present);

  if (present && !mounted) setMounted(true);
  useEffect(() => {
    if (present || !mounted) return undefined;
    const timer = globalThis.setTimeout(() => setMounted(false), EXIT_MS);

    return () => globalThis.clearTimeout(timer);
  }, [present, mounted]);

  return { mounted, leaving: mounted && !present };
};

/** Reports the sheet's height a frame after each change, so the first paint happens at 0 and the growth transitions. */
const observeHeight = (sheet: HTMLDivElement, setHeight: (height: number) => void): (() => void) => {
  let frame = 0;
  const observer = new globalThis.ResizeObserver(() => {
    globalThis.cancelAnimationFrame(frame);
    frame = globalThis.requestAnimationFrame(() => setHeight(sheet.offsetHeight));
  });

  observer.observe(sheet);

  return () => { observer.disconnect(); globalThis.cancelAnimationFrame(frame); };
};

/**
 * The height the dock should be: 0 when it arrives, so it grows up out of the
 * composer; the sheet's measured height while it lives, so rows coming and
 * going slide it; 0 again while it leaves. `null` means the browser cannot
 * measure (no ResizeObserver) and the dock keeps its natural height.
 */
export const useSheetHeight = (
  mounted: boolean, leaving: boolean,
): { sheetRef: RefObject<HTMLDivElement | null>; height: number | null } => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(0);

  useLayoutEffect(() => {
    const sheet = sheetRef.current;

    // Without a sheet the height rests at 0 for the next arrival; without an observer it stays natural.
    if (!sheet || typeof globalThis.ResizeObserver === 'undefined') {
      setHeight(sheet ? null : 0);

      return undefined;
    }

    return observeHeight(sheet, setHeight);
  }, [mounted]);

  return { sheetRef, height: leaving ? 0 : height };
};
